import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type { Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import { unwrap } from '@truecare/api-client';
import { Alert, Button, useFocusTrap } from '@truecare/ui';
import { api } from '../api';

/**
 * Homecare "add a card" modal — the self-service half of the Stripe rail
 * (the backend setup-intent / attach endpoints already exist; this is the
 * Stripe Elements front-end that was previously deferred).
 *
 * Flow (SetupIntent client_secret pattern):
 *   1. On open, POST /me/homecare/payment-methods/setup-intent → clientSecret.
 *   2. Mount <Elements> with that clientSecret + a <PaymentElement>.
 *   3. On submit, stripe.confirmSetup({ redirect: 'if_required' }) tokenizes
 *      the card against the SetupIntent (no redirect for plain cards).
 *   4. POST /me/homecare/payment-methods { stripePaymentMethodId, setAsDefault }
 *      to snapshot brand/last4/exp into our table (first card auto-defaults).
 *   5. Invalidate the homecare queries so the list + the /me
 *      hasDefaultPaymentMethod flag (Book-button gate) both refresh.
 *
 * Cards only (v1) — the backend rejects non-card PaymentMethods.
 */

export interface AddPaymentMethodModalProps {
  open: boolean;
  /** Loaded Stripe.js instance (from the publishable key). */
  stripePromise: Promise<Stripe | null>;
  /** Whether the newly-added card should become the default charge card.
   *  DA-M4 fix: the caller passes `true` only when there is no USABLE default
   *  (first card, or the current default is expired); otherwise `false` so a
   *  backup card is NOT silently promoted over the client's chosen default. The
   *  backend also auto-defaults the very first card regardless. */
  setAsDefault: boolean;
  onDone: () => void;
  onCancel: () => void;
}

export function AddPaymentMethodModal({
  open,
  stripePromise,
  setAsDefault,
  onDone,
  onCancel,
}: AddPaymentMethodModalProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useFocusTrap(panelRef, { active: open, onEscape: onCancel });

  // Create a fresh SetupIntent each time the modal opens.
  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setSetupError(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await unwrap(
          api.POST('/api/v1/me/homecare/payment-methods/setup-intent', {
            body: {},
          }),
        );
        if (!cancelled) setClientSecret(res.clientSecret);
      } catch (e) {
        if (!cancelled) {
          setSetupError(
            e instanceof Error
              ? e.message
              : "We couldn't start card setup. Please try again.",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, stripePromise]);

  if (!open) return null;

  const options: StripeElementsOptions | undefined = clientSecret
    ? { clientSecret, appearance: { theme: 'stripe' } }
    : undefined;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-payment-method-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      data-testid="add-payment-method-modal"
    >
      <div className="w-full max-w-md rounded-md border border-border bg-cream p-6 max-h-[90vh] overflow-y-auto">
        <h2
          id="add-payment-method-modal-title"
          className="font-heading text-xl text-forest"
        >
          Add a payment method
        </h2>
        <p className="mt-1 text-sm text-ink-muted">
          Visit charges are applied automatically to your default card — you can
          choose which card is your default after adding it. Your card details are
          handled by Stripe and never touch our servers.
        </p>

        {setupError && (
          <Alert
            tone="danger"
            className="mt-3"
            data-testid="add-payment-method-error"
          >
            {setupError}
          </Alert>
        )}

        {!clientSecret && !setupError && (
          <p
            className="mt-6 text-sm text-ink-muted"
            data-testid="add-payment-method-loading"
          >
            Preparing secure card entry…
          </p>
        )}

        {options && (
          <Elements stripe={stripePromise} options={options}>
            <AddCardForm
              onDone={onDone}
              onCancel={onCancel}
              setAsDefault={setAsDefault}
            />
          </Elements>
        )}

        {!options && (
          <div className="mt-6 flex items-center justify-end">
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Inner form — must live inside <Elements> to reach useStripe/useElements.
 */
function AddCardForm({
  onDone,
  onCancel,
  setAsDefault,
}: {
  onDone: () => void;
  onCancel: () => void;
  setAsDefault: boolean;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    // Re-entry guard — the disabled Save button is the primary defence, but
    // guard the function too (Enter-key / rapid activation can't double-confirm
    // the SetupIntent).
    if (submitting || !stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    const { error: confirmError, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(
        confirmError.message ??
          "We couldn't save your card. Please check the details and try again.",
      );
      setSubmitting(false);
      return;
    }
    if (
      !setupIntent ||
      setupIntent.status !== 'succeeded' ||
      !setupIntent.payment_method
    ) {
      setError('Card setup did not complete. Please try again.');
      setSubmitting(false);
      return;
    }

    try {
      await unwrap(
        api.POST('/api/v1/me/homecare/payment-methods', {
          body: {
            stripePaymentMethodId: String(setupIntent.payment_method),
            setAsDefault,
          },
        }),
      );
      // Refresh both the card list and the /me hasDefaultPaymentMethod flag.
      await qc.invalidateQueries({ queryKey: ['homecare'] });
      onDone();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'Your card was verified but saving it failed. Please try again.',
      );
      setSubmitting(false);
    }
  }

  return (
    <form
      className="mt-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <PaymentElement options={{ layout: 'tabs' }} />

      {error && (
        <Alert
          tone="danger"
          className="mt-3"
          data-testid="add-payment-method-form-error"
        >
          {error}
        </Alert>
      )}

      <div className="mt-5 flex items-center justify-end gap-3">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={submitting}
          disabled={!stripe || !elements || submitting}
          data-testid="add-payment-method-save"
        >
          Save card
        </Button>
      </div>
    </form>
  );
}
